import { GeneralSettingsSection } from "./general-settings-section";
import { NotificationsSection } from "./notifications-section";
import { CredentialsSection } from "./credentials-section";
import { DataControlsSection } from "./data-controls-section";
import { AuditExportSection, SecuritySection } from "./remaining-settings-sections";
import { ProfileSection } from "./settings-profile-section";
import { AppearanceSection } from "./settings-appearance-section";
import { ModelRoutesSection } from "./model-routes-section";
import { Check, Copy } from "lucide-react";
import {
  DEFAULT_SETTING_SLUG,
  canViewSettingSection,
  settingGroupOrder,
  settingSectionForSlug,
  visibleSettingSections,
  type SettingSection,
} from "./settings-sections";
import { DangerZoneSection } from "./project-settings-sections";
import { AgentsPolicySection, ApprovalsPolicySection, SpendPolicySection } from "./policy-sections";
import { RepositoriesPage } from "./repositories";
import { ArtifactsPage } from "./artifacts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Invitation, MembershipProject, ProjectMember } from "@zworks/console-contracts";
import * as React from "react";
import { Link, Navigate, useOutletContext, useParams, useSearchParams } from "react-router";
import { type ShellOutletContext } from "@/components/app-shell";
import { TenantMark } from "@/components/shell/project-switcher";
import { PageHeader } from "@/components/bespoke";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BillingSection } from "@/pages/billing-section";
import {
  ApiError,
  changeMemberRole,
  inviteMember,
  projectInvitationsQuery,
  projectMembersQuery,
  removeMember,
  resendInvitation,
  revokeInvitation,
  transferOwnership,
} from "@/lib/api";
import { formatDate, lastActiveLabel } from "@/lib/format";

const roleLabels: Record<ProjectMember["role"], string> = {
  owner: "Owner",
  developer: "Developer",
  approver: "Approver",
  viewer: "Viewer",
};

function failureOf(error: unknown): { message: string; requestId?: string } {
  const apiError =
    error instanceof ApiError ? error : new ApiError("The request could not be completed.", 500);
  return { message: apiError.problem?.detail ?? apiError.message, requestId: apiError.requestId };
}

/**
 * One member row, as the Members board draws it: the mark, the name, the last-active line, and
 * either a badge or, for the owner, the role picker with a remove button. A non-owner sees
 * badges only: the picker and the buttons are absent, not disabled.
 */
function MemberRow({
  projectId,
  member,
  canManage,
  notice,
  onDone,
}: {
  projectId: string;
  member: ProjectMember;
  canManage: boolean;
  notice: (message: string) => void;
  onDone: (message: string | null, failure: { message: string; requestId?: string } | null) => void;
}) {
  const queryClient = useQueryClient();
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  async function onRoleChange(role: ProjectMember["role"]) {
    if (role === member.role || saving) return;
    setSaving(true);
    onDone(null, null);
    try {
      await changeMemberRole(projectId, member.userId, { role });
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
      await queryClient.invalidateQueries({ queryKey: ["me", "projects"] });
      const article = role === "owner" || role === "approver" ? "an" : "a";
      notice(`${member.displayName} is now ${article} ${role}.`);
    } catch (error) {
      onDone(null, failureOf(error));
    } finally {
      setSaving(false);
    }
  }

  async function onRemove() {
    setConfirmingRemove(false);
    setSaving(true);
    onDone(null, null);
    try {
      await removeMember(projectId, member.userId);
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
      notice(`${member.displayName} was removed.`);
    } catch (error) {
      onDone(null, failureOf(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article
      aria-label={member.displayName}
      className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <TenantMark name={member.displayName} id={member.userId} className="size-9 text-xs" />
        <div className="min-w-0">
          <p className="truncate font-medium">
            {member.displayName}{" "}
            {member.isSelf ? (
              <span className="font-normal text-muted-foreground">(you)</span>
            ) : null}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {lastActiveLabel(member.lastActiveAt)}
          </p>
        </div>
      </div>
      {canManage && !member.isSelf ? (
        <div className="flex items-center gap-2">
          <select
            aria-label={`Role for ${member.displayName}`}
            value={member.role}
            disabled={saving}
            onChange={(event) => void onRoleChange(event.target.value as ProjectMember["role"])}
            className="h-8 rounded-md border border-input bg-card px-2 text-sm text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            {(Object.keys(roleLabels) as ProjectMember["role"][]).map((option) => (
              <option key={option} value={option}>
                {roleLabels[option]}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={saving}
            onClick={() => setConfirmingRemove(true)}
            aria-label={`Remove ${member.displayName}`}
          >
            Remove
          </Button>
        </div>
      ) : (
        <Badge variant={member.role === "owner" ? "outline" : "secondary"}>
          {roleLabels[member.role]}
        </Badge>
      )}
      {confirmingRemove ? (
        <Dialog open onOpenChange={(open) => setConfirmingRemove(open)}>
          <DialogContent className="w-[min(28rem,calc(100%-2rem))]">
            <DialogTitle>Remove {member.displayName}?</DialogTitle>
            <DialogDescription>
              They lose access to this project straight away, including its credits.
            </DialogDescription>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmingRemove(false)}>
                Keep them
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={saving}
                onClick={() => void onRemove()}
              >
                Remove member
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </article>
  );
}

interface CreatedLinkModal {
  email: string;
  url: string;
  delivered: boolean;
}

/**
 * Dialog shown right after creating or resending an invitation. Displays whether email delivery
 * succeeded and always surfaces the secure copy-link fallback for the owner.
 */
function InvitationLinkDialog({
  target,
  onClose,
}: {
  target: CreatedLinkModal | null;
  onClose: () => void;
}) {
  const [copiedUrl, setCopiedUrl] = React.useState<string | null>(null);

  if (!target) return null;
  const copied = copiedUrl === target.url;

  async function onCopy() {
    if (!target) return;
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
      await navigator.clipboard.writeText(target.url);
      setCopiedUrl(target.url);
    } catch {
      setCopiedUrl(null);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(32rem,calc(100%-2rem))]">
        <DialogTitle>Invitation ready</DialogTitle>
        <DialogDescription>
          {target.delivered
            ? `An invitation email was sent to ${target.email}. You can also copy the invite link below.`
            : `Email delivery is not configured or could not be completed. Share this invite link directly with ${target.email}.`}
        </DialogDescription>
        <div className="mt-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={target.url}
              aria-label="Invitation link"
              className="font-mono text-xs"
              onFocus={(e) => e.target.select()}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void onCopy()}
              className="shrink-0 gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="size-4" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="size-4" />
                  <span>Copy link</span>
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Anyone with this link can join this project with the selected role. Keep it secure.
          </p>
          <div className="mt-2 flex justify-end">
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One pending invitation, with its role, expiry, and the resend and revoke actions.
 */
function InvitationRow({
  projectId,
  invitation,
  onDone,
  onShowLink,
}: {
  projectId: string;
  invitation: Invitation;
  onDone: (message: string | null, failure: { message: string; requestId?: string } | null) => void;
  onShowLink: (link: CreatedLinkModal) => void;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = React.useState(false);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "invitations"] });
  }

  async function onResend() {
    if (saving) return;
    setSaving(true);
    onDone(null, null);
    try {
      const result = await resendInvitation(projectId, invitation.id);
      await refresh();
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const url = `${origin}/invitations/${encodeURIComponent(result.token)}`;
      const delivered = Boolean(result.delivered);
      onShowLink({ email: invitation.email, url, delivered });
      onDone(
        delivered
          ? `A fresh link is on its way to ${invitation.email}.`
          : `A fresh link was generated for ${invitation.email}.`,
        null,
      );
    } catch (error) {
      onDone(null, failureOf(error));
    } finally {
      setSaving(false);
    }
  }

  async function onRevoke() {
    if (saving) return;
    setSaving(true);
    onDone(null, null);
    try {
      await revokeInvitation(projectId, invitation.id);
      await refresh();
      onDone(`The invitation to ${invitation.email} was revoked.`, null);
    } catch (error) {
      onDone(null, failureOf(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article
      aria-label={invitation.email}
      className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed p-3"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <TenantMark name={invitation.email} id={invitation.id} className="size-9 text-xs" />
        <div className="min-w-0">
          <p className="truncate font-medium">{invitation.email}</p>
          <p className="truncate text-xs text-muted-foreground">
            {roleLabels[invitation.role]} · expires {formatDate(invitation.expiresAt)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="warning">Pending</Badge>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={saving}
          onClick={() => void onResend()}
          aria-label={`Resend invitation to ${invitation.email}`}
        >
          Resend
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={saving}
          onClick={() => void onRevoke()}
          aria-label={`Revoke invitation to ${invitation.email}`}
        >
          Revoke
        </Button>
      </div>
    </article>
  );
}

/** The transfer-ownership dialog. The caller becomes a viewer in the same step. */
function TransferDialog({
  projectId,
  candidates,
  open,
  onOpenChange,
  onDone,
}: {
  projectId: string;
  candidates: ProjectMember[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (message: string | null, failure: { message: string; requestId?: string } | null) => void;
}) {
  const queryClient = useQueryClient();
  const [newOwnerId, setNewOwnerId] = React.useState(candidates[0]?.userId ?? "");
  const [transferring, setTransferring] = React.useState(false);
  const chosen = candidates.find((row) => row.userId === newOwnerId) ?? candidates[0] ?? null;

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!chosen || transferring) return;
    setTransferring(true);
    onDone(null, null);
    try {
      await transferOwnership(projectId, { newOwnerId: chosen.userId });
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "members"] });
      await queryClient.invalidateQueries({ queryKey: ["me", "projects"] });
      onDone(`${chosen.displayName} is now the owner. You are a viewer.`, null);
      onOpenChange(false);
    } catch (error) {
      onDone(null, failureOf(error));
    } finally {
      setTransferring(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(30rem,calc(100%-2rem))]">
        <DialogTitle>Transfer ownership</DialogTitle>
        <DialogDescription>
          The new owner takes over billing and credits. You become a viewer.
        </DialogDescription>
        <form
          aria-label="Transfer ownership"
          className="mt-4 flex flex-col gap-4"
          onSubmit={onSubmit}
        >
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="new-owner">
              New owner
            </label>
            <select
              id="new-owner"
              value={chosen?.userId ?? ""}
              disabled={transferring}
              onChange={(event) => setNewOwnerId(event.target.value)}
              className="h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            >
              {candidates.map((row) => (
                <option key={row.userId} value={row.userId}>
                  {row.displayName} · {roleLabels[row.role]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={transferring}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!chosen || transferring}>
              {transferring ? "Transferring…" : "Transfer ownership"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The invite dialog. Surfaces email delivery status and the copy-link fallback. */
function InviteDialog({
  projectId,
  open,
  onOpenChange,
  onDone,
  onShowLink,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: (message: string | null, failure: { message: string; requestId?: string } | null) => void;
  onShowLink: (link: CreatedLinkModal) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<Invitation["role"]>("developer");
  const [inviting, setInviting] = React.useState(false);
  const valid = /.+@.+\..+/.test(email.trim());

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || inviting) return;
    setInviting(true);
    onDone(null, null);
    try {
      const result = await inviteMember(projectId, { email: email.trim(), role });
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "invitations"] });
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const url = `${origin}/invitations/${encodeURIComponent(result.token)}`;
      const delivered = Boolean(result.delivered);
      const invitedEmail = email.trim();
      setEmail("");
      onOpenChange(false);
      onShowLink({ email: invitedEmail, url, delivered });
      onDone(
        delivered
          ? `An invitation is on its way to ${invitedEmail}.`
          : `An invitation link was generated for ${invitedEmail}.`,
        null,
      );
    } catch (error) {
      onDone(null, failureOf(error));
    } finally {
      setInviting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(30rem,calc(100%-2rem))]">
        <DialogTitle>Invite members</DialogTitle>
        <DialogDescription>
          They share the project&apos;s credits. Only an owner can invite.
        </DialogDescription>
        <form aria-label="Invite members" className="mt-4 flex flex-col gap-4" onSubmit={onSubmit}>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="invite-email">
              Email address
            </label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.example"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="invite-role">
              Role
            </label>
            <select
              id="invite-role"
              value={role}
              disabled={inviting}
              onChange={(event) => setRole(event.target.value as Invitation["role"])}
              className="h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            >
              {(["developer", "approver", "viewer"] as const).map((option) => (
                <option key={option} value={option}>
                  {roleLabels[option]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={inviting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || inviting}>
              {inviting ? "Inviting…" : "Send invitation"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The settings content shared by the page and the modal: the grouped section list on the left and
 * the section on the right. Sections are links to /settings/:section so every section is
 * deep-linkable and the sidebar highlight follows the route. Usage and the audit log live outside
 * settings now; owner-only sections never render for other roles, except Spend and budgets,
 * which developers may also view read-only.
 */
export function SettingsView({
  project,
  active,
  headingLevel = "page",
  surface = "page",
  onOpenAsPage,
  onSelectSection,
}: {
  project: MembershipProject;
  active: SettingSection;
  headingLevel?: "page" | "dialog";
  surface?: "page" | "dialog";
  onOpenAsPage?: (slug: string) => void;
  onSelectSection?: (slug: string) => void;
}) {
  const [inviting, setInviting] = React.useState(false);
  const [transferring, setTransferring] = React.useState(false);
  const [createdLink, setCreatedLink] = React.useState<CreatedLinkModal | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [failure, setFailure] = React.useState<{ message: string; requestId?: string } | null>(
    null,
  );

  const projectId = project.id;
  const isOwner = project.role === "owner";
  const visible = visibleSettingSections(project.role, surface);
  const section = active.slug;
  const [searchParams] = useSearchParams();
  const members = useQuery({
    ...projectMembersQuery(projectId),
    enabled: isOwner && (section === "members" || section === "plan-billing"),
  });

  const list = members.data ?? [];
  const pendingInvitations = useQuery({
    ...projectInvitationsQuery(projectId),
    enabled: isOwner && section === "members",
  });

  function onDone(message: string | null, next: { message: string; requestId?: string } | null) {
    setNotice(message);
    setFailure(next);
  }

  if (!canViewSettingSection(project.role, active)) {
    return (
      <ForbiddenState
        title={`${active.label} is only visible to owners`}
        description="Ask a project owner if you need these details."
      />
    );
  }

  const SectionTag = headingLevel === "dialog" ? "h3" : "h2";

  return (
    <>
      {headingLevel === "page" ? (
        <PageHeader
          title="Project settings"
          description={`${project.name} · you are ${roleLabels[project.role].toLowerCase()}`}
          actions={
            isOwner && section === "members" ? (
              <Button type="button" onClick={() => setInviting(true)}>
                Invite members
              </Button>
            ) : undefined
          }
        />
      ) : null}

      <div className="flex flex-col gap-6 lg:flex-row">
        <nav
          aria-label="Settings sections"
          className="flex gap-4 overflow-x-auto lg:w-56 lg:shrink-0 lg:flex-col lg:gap-6 lg:overflow-visible"
        >
          {settingGroupOrder.map((group) => {
            const items = visible.filter((entry) => entry.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="flex shrink-0 flex-col gap-1 lg:shrink">
                <p className="px-3 pt-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  {group}
                </p>
                {items.map(({ slug, label, icon: Icon }) =>
                  onSelectSection ? (
                    <button
                      key={slug}
                      type="button"
                      aria-current={section === slug ? "page" : undefined}
                      onClick={() => onSelectSection(slug)}
                      className={cn(
                        "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm whitespace-nowrap transition-colors",
                        section === slug
                          ? "bg-selected font-semibold text-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground",
                      )}
                    >
                      <Icon
                        className={cn("size-4 shrink-0", section === slug && "text-accent-text")}
                      />
                      <span>{label}</span>
                    </button>
                  ) : (
                    <Link
                      key={slug}
                      to={`/settings/${slug}`}
                      aria-current={section === slug ? "page" : undefined}
                      className={cn(
                        "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm whitespace-nowrap transition-colors",
                        section === slug
                          ? "bg-selected font-semibold text-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground",
                      )}
                    >
                      <Icon
                        className={cn("size-4 shrink-0", section === slug && "text-accent-text")}
                      />
                      <span>{label}</span>
                    </Link>
                  ),
                )}
              </div>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 flex flex-col gap-6">
          {headingLevel === "dialog" ? (
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold tracking-tight">{active.label}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {project.name} · you are {roleLabels[project.role].toLowerCase()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isOwner && section === "members" ? (
                  <Button type="button" size="sm" onClick={() => setInviting(true)}>
                    Invite members
                  </Button>
                ) : null}
                {onOpenAsPage ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenAsPage(active.slug)}
                  >
                    Open as page
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {section === "tokens" ? (
            <CredentialsSection key={projectId} projectId={projectId} role={project.role} />
          ) : section === "plan-billing" ? (
            <BillingSection projectId={projectId} />
          ) : section === "general" ? (
            <GeneralSettingsSection
              key={projectId}
              projectId={projectId}
              isOwner={project.role === "owner"}
            />
          ) : section === "notifications" ? (
            <NotificationsSection key={projectId} projectId={projectId} />
          ) : section === "data-retention" ? (
            <DataControlsSection
              key={projectId}
              projectId={projectId}
              isOwner={project.role === "owner"}
              downloadExportId={searchParams.get("export")}
            />
          ) : section === "profile" ? (
            <ProfileSection project={project} />
          ) : section === "appearance" ? (
            <AppearanceSection />
          ) : section === "repositories" ? (
            <RepositoriesPage headingLevel={headingLevel === "dialog" ? "h2" : "h1"} />
          ) : section === "artifacts" ? (
            <ArtifactsPage />
          ) : section === "approvals" ? (
            <ApprovalsPolicySection
              key={`${projectId}-approvals`}
              projectId={projectId}
              isOwner={project.role === "owner"}
            />
          ) : section === "spend" ? (
            <SpendPolicySection
              key={`${projectId}-spend`}
              projectId={projectId}
              isOwner={project.role === "owner"}
            />
          ) : section === "agents" ? (
            <AgentsPolicySection
              key={`${projectId}-agents`}
              projectId={projectId}
              isOwner={project.role === "owner"}
            />
          ) : section === "model-routes" ? (
            <ModelRoutesSection
              key={`${projectId}-model-routes`}
              projectId={projectId}
              role={project.role}
            />
          ) : section === "security" ? (
            <SecuritySection key={`${projectId}-security`} />
          ) : section === "audit-export" ? (
            <AuditExportSection key={`${projectId}-audit-export`} projectId={projectId} />
          ) : section === "danger-zone" ? (
            <DangerZoneSection projectName={project.name} />
          ) : section !== "members" ? (
            <EmptyState title="Unknown section" description="Choose a section from the list." />
          ) : members.isPending ? (
            <LoadingState label="Loading members" rows={4} />
          ) : members.isError ? (
            members.error instanceof ApiError && members.error.status === 403 ? (
              <ForbiddenState
                title="Members are hidden"
                description="Only members of this project can see its members."
              />
            ) : (
              <ErrorState
                title="Members could not be loaded"
                description={
                  members.error instanceof ApiError
                    ? (members.error.problem?.detail ?? members.error.message)
                    : "The request could not be completed."
                }
                requestId={
                  members.error instanceof ApiError
                    ? (members.error.requestId ?? "unknown")
                    : "unknown"
                }
                onRetry={() => void members.refetch()}
              />
            )
          ) : (
            <div className="flex flex-col gap-6">
              {notice ? (
                <p role="status" className="text-sm text-muted-foreground">
                  {notice}
                </p>
              ) : null}
              {failure ? (
                <div role="alert" className="flex flex-col gap-0.5 text-[0.8125rem]">
                  <p className="font-medium text-danger">{failure.message}</p>
                  {failure.requestId ? (
                    <p className="text-muted-foreground">
                      Reference{" "}
                      <span className="font-mono text-foreground">{failure.requestId}</span>
                    </p>
                  ) : null}
                </div>
              ) : null}

              <section aria-label="Members">
                <SectionTag className="text-lg font-semibold tracking-tight">Members</SectionTag>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {list.length} {list.length === 1 ? "person" : "people"} · they share the
                  project&apos;s credits
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  {list.map((member) => (
                    <MemberRow
                      key={member.userId}
                      projectId={projectId}
                      member={member}
                      canManage={isOwner}
                      notice={setNotice}
                      onDone={onDone}
                    />
                  ))}
                </div>
              </section>

              {isOwner ? (
                <section aria-label="Invitations">
                  <SectionTag className="text-lg font-semibold tracking-tight">
                    Invitations
                  </SectionTag>
                  {pendingInvitations.isPending ? (
                    <div className="mt-3">
                      <LoadingState label="Loading invitations" rows={1} />
                    </div>
                  ) : pendingInvitations.isError ? (
                    <p role="alert" className="mt-3 text-sm text-danger">
                      {pendingInvitations.error instanceof ApiError
                        ? (pendingInvitations.error.problem?.detail ??
                          pendingInvitations.error.message)
                        : "The request could not be completed."}
                    </p>
                  ) : (pendingInvitations.data ?? []).length === 0 ? (
                    <p className="mt-3 text-sm text-muted-foreground">No pending invitations.</p>
                  ) : (
                    <div className="mt-3 flex flex-col gap-2">
                      {(pendingInvitations.data ?? []).map((invitation) => (
                        <InvitationRow
                          key={invitation.id}
                          projectId={projectId}
                          invitation={invitation}
                          onDone={onDone}
                          onShowLink={setCreatedLink}
                        />
                      ))}
                    </div>
                  )}
                </section>
              ) : null}

              {isOwner ? (
                <section aria-label="Ownership">
                  <SectionTag className="text-lg font-semibold tracking-tight">
                    Ownership
                  </SectionTag>
                  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border p-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">Transfer ownership</p>
                      <p className="text-sm text-muted-foreground">
                        The new owner takes over billing and credits. You become a viewer.
                      </p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => setTransferring(true)}>
                      Transfer ownership
                    </Button>
                  </div>
                  <div className="mt-3 flex gap-3 rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium">Roles</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Developer starts runs and edits flows. Approver decides approvals. Viewer
                        reads. Nobody decides a change they started.
                      </p>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {isOwner ? (
        <>
          <InviteDialog
            projectId={projectId}
            open={inviting}
            onOpenChange={setInviting}
            onDone={onDone}
            onShowLink={setCreatedLink}
          />
          <TransferDialog
            projectId={projectId}
            candidates={list.filter((row) => !row.isSelf && row.role !== "owner")}
            open={transferring}
            onOpenChange={setTransferring}
            onDone={onDone}
          />
          <InvitationLinkDialog target={createdLink} onClose={() => setCreatedLink(null)} />
        </>
      ) : null}
    </>
  );
}

/** Redirects /settings and legacy ?section= URLs to the deep-linked section routes. */
export function SettingsIndexRedirect() {
  const [searchParams] = useSearchParams();
  const legacy = searchParams.get("section")?.toLowerCase() ?? "";
  const map: Record<string, string> = {
    general: "general",
    members: "members",
    "members and roles": "members",
    "billing and credits": "plan-billing",
    billing: "plan-billing",
    "plan-and-billing": "plan-billing",
    "plan and billing": "plan-billing",
    credentials: "tokens",
    tokens: "tokens",
    notifications: "notifications",
    "data controls": "data-retention",
    "data-controls": "data-retention",
    "data and retention": "data-retention",
  };
  if (legacy === "usage") return <Navigate to="/usage" replace />;
  if (legacy === "audit log" || legacy === "audit") return <Navigate to="/audit" replace />;
  return <Navigate to={`/settings/${map[legacy] ?? DEFAULT_SETTING_SLUG}`} replace />;
}

/** Reads the section slug from the route and renders the settings page. */
export function SettingsPage() {
  const { currentProject } = useOutletContext<ShellOutletContext>();
  const { section } = useParams<{ section?: string }>();
  const active = settingSectionForSlug(section);

  if (!currentProject) {
    return (
      <>
        <PageHeader title="Settings" />
        <EmptyState
          title="No project chosen"
          description="Choose a project to manage its settings."
        />
      </>
    );
  }

  if (!active) {
    return <Navigate to="/settings/general" replace />;
  }

  if (active.scope === "account") {
    return <Navigate to="/settings/general" replace />;
  }

  if (!canViewSettingSection(currentProject.role, active)) {
    return <Navigate to="/settings/general" replace />;
  }

  if (
    currentProject.role === "viewer" &&
    active.slug !== "general" &&
    active.slug !== "artifacts"
  ) {
    return <Navigate to="/settings/general" replace />;
  }

  return <SettingsView project={currentProject} active={active} headingLevel="page" />;
}
